import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { formatPriceWithCurrency } from '@/utils/currency';
import { useCurrency } from '@/hooks/useCurrency';
import { 
  DollarSign, CheckCircle, XCircle, Clock, RefreshCw, 
  CreditCard, Smartphone, AlertCircle, FileText, Search
} from 'lucide-react';
import { toast } from 'sonner';

interface WithdrawalRequest {
  id: string;
  vendor_id: string;
  amount_cents: number;
  currency: string;
  payment_method: 'bank_transfer' | 'mobile_money' | 'other';
  bank_name: string | null;
  account_number: string | null;
  account_name: string | null;
  mobile_money_provider: string | null;
  mobile_money_number: string | null;
  payment_notes: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'cancelled';
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  completed_at: string | null;
  admin_notes: string | null;
  rejection_reason: string | null;
  transaction_reference: string | null;
  profiles?: {
    full_name: string;
    business_name: string | null;
  };
}

interface WithdrawalRpcResult {
  success?: boolean;
  error?: string;
  message?: string;
}

export const AdminWithdrawalManager = () => {
  const { displayCurrency, convertPrice } = useCurrency();
  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequest | null>(null);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [reviewForm, setReviewForm] = useState({
    action: 'approve' as 'approve' | 'reject',
    admin_notes: '',
    rejection_reason: '',
    transaction_reference: '',
    b2cCurrency: 'CDF' as 'CDF' | 'USD'
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('vendor_withdrawal_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch vendor profiles separately
      const vendorIds = [...new Set((data || []).map((r: any) => r.vendor_id).filter(Boolean))];
      let profilesMap: Record<string, { full_name: string; business_name: string }> = {};
      if (vendorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name, business_name')
          .in('id', vendorIds);
        (profiles || []).forEach((p: any) => { profilesMap[p.id] = p; });
      }

      const enriched = (data || []).map((r: any) => ({
        ...r,
        profiles: profilesMap[r.vendor_id] || null
      }));

      setRequests(enriched as WithdrawalRequest[]);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const generateRef = () => {
    const now = new Date();
    const datePart = now.toISOString().slice(0, 10).replace(/-/g, '');
    const rand = Math.floor(1000 + Math.random() * 9000);
    return `WD-${datePart}-${rand}`;
  };

  const handleReviewRequest = (request: WithdrawalRequest) => {
    setSelectedRequest(request);
    setReviewForm({
      action: 'approve',
      admin_notes: '',
      rejection_reason: '',
      transaction_reference: generateRef(),
      b2cCurrency: 'CDF'
    });
    setShowReviewDialog(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedRequest) return;

    try {
      setProcessing(true);

      if (reviewForm.action === 'approve') {
        const { data, error } = await supabase.functions.invoke('process-withdrawal', {
          body: {
            requestId: selectedRequest.id,
            adminNotes: reviewForm.admin_notes || null,
            b2cCurrency: selectedRequest.payment_method === 'mobile_money' ? reviewForm.b2cCurrency : undefined,
          }
        });

        if (error) throw error;
        if (data?.success === false) throw new Error(data.error || 'Erreur lors de l\'approbation');

        const statusMsg = data?.status === 'processing'
          ? `Demande approuvée — ${data.message}`
          : `Retrait effectué avec succès (${data.transactionRef})`;
        toast.success(statusMsg);
      } else {
        if (!reviewForm.rejection_reason.trim()) {
          toast.error('Veuillez fournir une raison de refus');
          return;
        }

        const { data, error } = await supabase.rpc('reject_withdrawal_request', {
          p_request_id: selectedRequest.id,
          p_rejection_reason: reviewForm.rejection_reason,
          p_admin_notes: reviewForm.admin_notes || null
        });

        if (error) throw error;
        if ((data as WithdrawalRpcResult | null)?.success === false) {
          throw new Error((data as WithdrawalRpcResult).error || 'Erreur lors du refus');
        }

        toast.success((data as WithdrawalRpcResult | null)?.message || 'Demande refusee');
      }

      setShowReviewDialog(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error: any) {
      console.error('Error processing request:', error);
      toast.error(error.message || 'Erreur lors du traitement');
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkCompleted = async (request: WithdrawalRequest) => {
    if (!window.confirm(`Marquer ce retrait comme complété ? Cette action est irréversible.`)) return;
    try {
      setProcessing(true);
      const { error } = await supabase
        .from('vendor_withdrawal_requests')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          admin_notes: 'Marqué complété manuellement par admin (transfert confirmé MaishaPay)',
          updated_at: new Date().toISOString(),
        })
        .eq('id', request.id)
        .eq('status', 'processing');
      if (error) throw error;
      toast.success('Retrait marqué comme complété');
      fetchRequests();
    } catch (error: any) {
      toast.error(error.message || 'Erreur');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: any; label: string; icon: any }> = {
      pending: { variant: 'secondary', label: 'En attente', icon: Clock },
      processing: { variant: 'default', label: 'En traitement', icon: RefreshCw },
      approved: { variant: 'default', label: 'Approuvé', icon: CheckCircle },
      completed: { variant: 'default', label: 'Complété', icon: CheckCircle },
      rejected: { variant: 'destructive', label: 'Refusé', icon: XCircle },
      cancelled: { variant: 'outline', label: 'Annulé', icon: XCircle }
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1 w-fit">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter(request => {
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const vendorName = request.profiles?.business_name || request.profiles?.full_name || '';
    const matchesSearch = vendorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.transaction_reference?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const stats = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved' || r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    totalAmount: requests
      .filter(r => r.status === 'approved' || r.status === 'completed')
      .reduce((sum, r) => sum + r.amount_cents, 0)
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En attente</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
            <p className="text-xs text-muted-foreground">À traiter</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approuvés</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approved}</div>
            <p className="text-xs text-muted-foreground">Traités avec succès</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Refusés</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.rejected}</div>
            <p className="text-xs text-muted-foreground">Rejetés</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Payé</CardTitle>
            <DollarSign className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPriceWithCurrency(stats.totalAmount, 'CDF', displayCurrency, convertPrice)}
            </div>
            <p className="text-xs text-muted-foreground">Depuis le début</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par vendeur ou référence..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="all">Tous</TabsTrigger>
                <TabsTrigger value="pending">En attente</TabsTrigger>
                <TabsTrigger value="approved">Approuvés</TabsTrigger>
                <TabsTrigger value="rejected">Refusés</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Demandes de retrait</CardTitle>
              <CardDescription>Gérer les demandes de retrait des vendeurs</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchRequests}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Aucune demande trouvée</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Vendeur</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Méthode</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">
                        {new Date(request.requested_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {request.profiles?.business_name || request.profiles?.full_name || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell className="font-bold text-lg">
                        {formatPriceWithCurrency(
                          request.amount_cents,
                          request.currency,
                          displayCurrency,
                          convertPrice
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {request.payment_method === 'bank_transfer' && (
                            <>
                              <CreditCard className="h-4 w-4" />
                              <div>
                                <div className="font-medium">{request.bank_name}</div>
                                <div className="text-xs text-muted-foreground">{request.account_number}</div>
                                <div className="text-xs text-muted-foreground">{request.account_name}</div>
                              </div>
                            </>
                          )}
                          {request.payment_method === 'mobile_money' && (
                            <>
                              <Smartphone className="h-4 w-4" />
                              <div>
                                <div className="font-medium">{request.mobile_money_provider}</div>
                                <div className="text-xs text-muted-foreground">{request.mobile_money_number}</div>
                              </div>
                            </>
                          )}
                        </div>
                        {request.payment_notes && (
                          <div className="text-xs text-muted-foreground mt-1 italic">
                            {request.payment_notes}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(request.status)}
                        {request.transaction_reference && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Réf: {request.transaction_reference}
                          </div>
                        )}
                        {request.rejection_reason && (
                          <div className="text-xs text-destructive mt-1">
                            {request.rejection_reason}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {request.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleReviewRequest(request)}
                          >
                            Traiter
                          </Button>
                        )}
                        {request.status === 'processing' && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={processing}
                            onClick={() => handleMarkCompleted(request)}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Marquer complété
                          </Button>
                        )}
                        {request.status !== 'pending' && request.status !== 'processing' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowReviewDialog(true);
                            }}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedRequest?.status === 'pending' 
                ? 'Traiter la demande de retrait'
                : 'Détails de la demande'
              }
            </DialogTitle>
            <DialogDescription>
              {selectedRequest?.profiles?.business_name || selectedRequest?.profiles?.full_name}
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* Request Details */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Montant:</span>
                  <span className="font-bold text-lg">
                    {formatPriceWithCurrency(
                      selectedRequest.amount_cents,
                      selectedRequest.currency,
                      displayCurrency,
                      convertPrice
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Date de demande:</span>
                  <span className="font-medium">
                    {new Date(selectedRequest.requested_at).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Statut:</span>
                  {getStatusBadge(selectedRequest.status)}
                </div>
              </div>

              {/* Payment Details */}
              <div className="border rounded-lg p-4 space-y-2">
                <h4 className="font-semibold mb-2">Informations de paiement</h4>
                {selectedRequest.payment_method === 'bank_transfer' && (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">Banque:</span>
                      <span className="font-medium">{selectedRequest.bank_name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">N° de compte:</span>
                      <span className="font-medium">{selectedRequest.account_number}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">Titulaire:</span>
                      <span className="font-medium">{selectedRequest.account_name}</span>
                    </div>
                  </>
                )}
                {selectedRequest.payment_method === 'mobile_money' && (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">Opérateur:</span>
                      <span className="font-medium">{selectedRequest.mobile_money_provider}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-muted-foreground">Numéro:</span>
                      <span className="font-medium">{selectedRequest.mobile_money_number}</span>
                    </div>
                  </>
                )}
                {selectedRequest.payment_notes && (
                  <div className="text-sm mt-2 p-2 bg-muted rounded">
                    <span className="text-muted-foreground">Notes:</span>
                    <p className="mt-1">{selectedRequest.payment_notes}</p>
                  </div>
                )}
              </div>

              {/* Review Form (only for pending requests) */}
              {selectedRequest.status === 'pending' && (
                <>
                  <div className="space-y-2">
                    <Label>Action</Label>
                    <Tabs value={reviewForm.action} onValueChange={(v: any) => setReviewForm({ ...reviewForm, action: v })}>
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="approve">Approuver</TabsTrigger>
                        <TabsTrigger value="reject">Refuser</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>

                  {reviewForm.action === 'approve' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="transaction_ref">Référence de transaction</Label>
                        <div className="flex gap-2">
                          <Input
                            id="transaction_ref"
                            value={reviewForm.transaction_reference}
                            readOnly
                            className="font-mono text-sm bg-muted"
                          />
                          <button
                            type="button"
                            className="px-3 py-1 text-xs border rounded hover:bg-muted"
                            onClick={() => setReviewForm({ ...reviewForm, transaction_reference: generateRef() })}
                          >↺</button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Référence interne auto-générée — modifiable si nécessaire
                        </p>
                      </div>

                      {selectedRequest?.payment_method === 'mobile_money' && (
                        <div className="space-y-2">
                          <Label>Wallet MaishaPay B2C</Label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, b2cCurrency: 'CDF' })}
                              className={`flex-1 py-2 text-sm font-medium rounded border transition-colors ${
                                reviewForm.b2cCurrency === 'CDF'
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-muted text-muted-foreground border-input hover:bg-accent'
                              }`}
                            >
                              CDF
                            </button>
                            <button
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, b2cCurrency: 'USD' })}
                              className={`flex-1 py-2 text-sm font-medium rounded border transition-colors ${
                                reviewForm.b2cCurrency === 'USD'
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'bg-muted text-muted-foreground border-input hover:bg-accent'
                              }`}
                            >
                              USD
                            </button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {reviewForm.b2cCurrency === 'USD'
                              ? `Utilisera le sous-wallet USD (~$${(selectedRequest.amount_cents / 100 * 0.0004).toFixed(2)} USD)`
                              : `Utilisera le sous-wallet CDF (${(selectedRequest.amount_cents / 100).toFixed(0)} CDF)`}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {reviewForm.action === 'reject' && (
                    <div className="space-y-2">
                      <Label htmlFor="rejection_reason">Raison du refus *</Label>
                      <Textarea
                        id="rejection_reason"
                        value={reviewForm.rejection_reason}
                        onChange={(e) => setReviewForm({ ...reviewForm, rejection_reason: e.target.value })}
                        placeholder="Expliquez pourquoi cette demande est refusée..."
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="admin_notes">Notes internes (optionnel)</Label>
                    <Textarea
                      id="admin_notes"
                      value={reviewForm.admin_notes}
                      onChange={(e) => setReviewForm({ ...reviewForm, admin_notes: e.target.value })}
                      placeholder="Notes pour référence interne..."
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Existing Details for processed requests */}
              {selectedRequest.status !== 'pending' && (
                <>
                  {selectedRequest.transaction_reference && (
                    <div className="space-y-2">
                      <Label>Référence de transaction</Label>
                      <Input value={selectedRequest.transaction_reference} readOnly />
                    </div>
                  )}
                  {selectedRequest.rejection_reason && (
                    <div className="space-y-2">
                      <Label>Raison du refus</Label>
                      <Textarea value={selectedRequest.rejection_reason} readOnly rows={3} />
                    </div>
                  )}
                  {selectedRequest.admin_notes && (
                    <div className="space-y-2">
                      <Label>Notes administrateur</Label>
                      <Textarea value={selectedRequest.admin_notes} readOnly rows={2} />
                    </div>
                  )}
                  {selectedRequest.reviewed_at && (
                    <div className="text-sm text-muted-foreground">
                      Traité le {new Date(selectedRequest.reviewed_at).toLocaleString('fr-FR')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowReviewDialog(false);
                setSelectedRequest(null);
              }}
            >
              {selectedRequest?.status === 'pending' ? 'Annuler' : 'Fermer'}
            </Button>
            {selectedRequest?.status === 'pending' && (
              <Button onClick={handleSubmitReview} disabled={processing}>
                {processing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Traitement...
                  </>
                ) : reviewForm.action === 'approve' ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approuver
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Refuser
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
